import React, { Suspense, useEffect, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { useNavigate, useParams } from 'react-router-dom';
import { StringParam, useQueryParam } from 'use-query-params';
import { Box, Fab } from '@mui/material';
import { Add } from '@mui/icons-material';
import { CarModel, StockStatus } from 'models/StockModel';
import { CompanyType } from 'models/CompanyModel';
import useCars from 'modules/cars/hooks/useCars';
import useCarModals from 'modules/cars/hooks/useCarModals';
import ProblemMark from 'modules/cars/common/ProblemMark';
import CarDesktop from 'modules/cars/views/CarDesktop';
import CarMobile from 'modules/cars/views/CarMobile';
import CarPortAgentDesktop from 'modules/cars/views/CarPortAgentDesktop';
import CarPortAgentMobile from 'modules/cars/views/CarPortAgentMobile';
import carListFamily from 'recoil/atoms/carListFamily';
import auth from 'recoil/atoms/auth';
import { isMobile } from 'react-device-detect';


const CarInner = () => {
    const my = useRecoilValue(auth);
    const navigate = useNavigate();
    const {id} = useParams();
    const [docPreview] = useQueryParam('doc', StringParam);
    const {getCar} = useCars();
    const {openAddCarModal} = useCarModals();
    const cars = useRecoilValue(carListFamily('pending'));
    const [car, setCar] = useState<CarModel>();

    useEffect(() => {
        const car = cars.find(u => u.id === Number(id));

        if (car) {
            setCar(car);
        } else {
            getCar(Number(id))
                .then(setCar)
                .catch(() => navigate('/cars/pending'));
        }
    }, [cars, getCar, id, navigate]);

    if (!car)
        return null;

    let title = <>
        {car.make || 'Unknown'} {car.model || 'Car'}, {car.chassisNumber}
        {' '}
        <ProblemMark car={car}/>
    </>;

    const canEdit = car.status === StockStatus.Pending || my.type === CompanyType.Master;

    if (isMobile) {
        const View = my.type === CompanyType.PortAgent ? CarPortAgentMobile : CarMobile;

        return (
            <>
                <View
                    my={my}
                    car={car}
                    title={title}
                    canEdit={canEdit}
                />

                <FabCreate
                    can={my.permissions.has('cars create')}
                    action={openAddCarModal}
                />
            </>
        )
    }

    const View = my.type === CompanyType.PortAgent ? CarPortAgentDesktop : CarDesktop;
    const padding = my.type === CompanyType.PortAgent ? {pl: 2} : {pl: 0};

    return (
        <Box sx={padding}>
            <View
                my={my}
                car={car}
                title={title}
                canEdit={canEdit}
                docPreview={docPreview}
                current={Number(id)}
            />

            <FabCreate
                can={my.permissions.has('cars create')}
                action={openAddCarModal}
            />
        </Box>
    );
};


type FabCreateProps = {
    can: boolean,
    action: () => void,
}

const FabCreate = ({can, action}: FabCreateProps) => {
    if (!can)
        return null;
    return (
        <Fab
            color="primary"
            aria-label="add car"
            sx={{
                position: "fixed",
                bottom: theme => theme.spacing(2),
                right: theme => theme.spacing(2)
            }}
            onClick={action}
        >
            <Add/>
        </Fab>
    )
}

const Car = () => {
    return (
        <Suspense fallback={null}>
            <CarInner/>
        </Suspense>
    )
};

export default Car;
